import { PageHero } from "@/components/PageHero";
import SmartImage from "@/components/SmartImage";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Section } from "@/components/Section";
import { toast } from "sonner";

const TYPES = ["Technical", "Academy", "Sponsor"];

export default function Partners() {
  const [partners, setPartners] = useState([]);
  useEffect(() => { api.get("/partners").then((r) => setPartners(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="partners-page">
      <PageHero eyebrow="Building Together" title="Our Partners" image="arena" position="center 40%" testId="partners-hero">
        Ironveil Era works with organizations helping develop combat sports in India — through technology, academies and sponsorship.
      </PageHero>

      {TYPES.map((t) => {
        const list = partners.filter((p) => p.category === t);
        return (
          <Section key={t} eyebrow={`0${TYPES.indexOf(t) + 1} / ${t}`} title={t === "Sponsor" ? "Sponsors" : `${t} Partners`} testId={`partners-${t.toLowerCase()}`}>
            {list.length ? (
              <div className={`grid gap-4 ${t === "Sponsor" ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" : "md:grid-cols-2"}`}>
                {list.map((p, i) => t === "Sponsor" ? <LogoCard key={p.id} p={p} /> : t === "Academy" ? <AcademyCard key={p.id} p={p} index={i} /> : <PartnerCard key={p.id} p={p} index={i} type={t} />)}
              </div>
            ) : (
              <div className="card-ive p-10 text-center text-[#888]">To be announced.</div>
            )}
          </Section>
        );
      })}

      <Section title="Become a Partner" eyebrow="Enquiry" testId="partner-enquiry-section">
        <PartnerForm />
      </Section>
    </div>
  );
}

function LogoCard({ p }) {
  return (
    <div className="card-ive h-24 flex items-center justify-center text-center px-4">
      <span className="font-display text-2xl">{p.name}</span>
    </div>
  );
}
function AcademyCard({ p, index }) {
  return (
    <div className="card-ive p-6" data-testid={`partner-card-${p.id}`}>
      <div className="type-meta text-[#C8102E]">Academy · {String(index + 1).padStart(2, "0")}</div>
      <div className="type-card mt-2">{p.name}</div>
      {p.location && <div className="type-meta text-[#888] mt-2">{p.location}</div>}
    </div>
  );
}

function PartnerCard({ p, index, type }) {
  return (
    <div className="card-ive p-6 flex gap-5 items-start" data-testid={`partner-card-${p.id}`}>
      {(p.photo_path || p.logo_path) && (
        <SmartImage path={p.photo_path || p.logo_path} fallback="fighter" alt={p.coach_name || p.name} className="w-20 h-20 sm:w-24 sm:h-24 object-cover border border-[#2a2a2a] shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <div className="type-meta text-[#C8102E]">{type} Partner · {String(index + 1).padStart(2, "0")}</div>
        <div className="type-card mt-2">{p.name}</div>
        {p.coach_name && <div className="text-sm font-semibold mt-2">Head Coach — {p.coach_name}</div>}
        <div className="type-meta text-[#888] mt-1">{[p.sport, p.location || "India"].filter(Boolean).join(" · ")}</div>
        {p.description && <p className="text-sm text-[#d0d0d0] mt-3 leading-relaxed">{p.description}</p>}
      </div>
    </div>
  );
}

function PartnerForm() {
  const [f, setF] = useState({ organization:"", contact_person:"", email:"", phone:"", partnership_type:"Sponsor", city:"", website:"", message:"" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/partner-enquiries", f);
      setDone(true);
      toast.success("Enquiry sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally { setLoading(false); }
  };

  if (done) return (
    <div className="card-ive p-10 text-center" data-testid="partner-enquiry-success">
      <div className="type-label text-[#C8102E] mb-2">Received</div>
      <div className="font-display text-2xl">Enquiry Submitted</div>
      <p className="mt-3 text-[#a0a0a0]">Our team will respond shortly at {f.email}.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="grid md:grid-cols-2 gap-4 max-w-4xl" data-testid="partner-form">
      <Input label="Organization" value={f.organization} onChange={(v) => set("organization", v)} required testid="p-org" />
      <Input label="Contact Person" value={f.contact_person} onChange={(v) => set("contact_person", v)} required testid="p-name" />
      <Input label="Email" type="email" value={f.email} onChange={(v) => set("email", v)} required testid="p-email" />
      <Input label="Phone" value={f.phone} onChange={(v) => set("phone", v)} required testid="p-phone" />
      <Select label="Partnership Type" value={f.partnership_type} onChange={(v) => set("partnership_type", v)} options={["Sponsor","Technical","Academy","Other"]} testid="p-type" />
      <Input label="City" value={f.city} onChange={(v) => set("city", v)} testid="p-city" />
      <Input label="Website / Social" value={f.website} onChange={(v) => set("website", v)} testid="p-website" />
      <div className="md:col-span-2">
        <Field label="Message">
          <textarea rows={5} value={f.message} onChange={(e) => set("message", e.target.value)} required className="w-full px-4 py-3" data-testid="p-message" />
        </Field>
      </div>
      <div className="md:col-span-2">
        <button disabled={loading} className="btn-primary" data-testid="p-submit">{loading ? "Sending…" : "Submit Enquiry"}</button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="type-meta text-[#888] mb-2">{label}</div>
      {children}
    </label>
  );
}
function Input({ label, value, onChange, required, type = "text", testid }) {
  return <Field label={label}><input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} className="w-full px-4 py-3" data-testid={testid} /></Field>;
}
function Select({ label, value, onChange, options, testid }) {
  return (
    <Field label={label}>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-4 py-3" data-testid={testid}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </Field>
  );
}
